import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Form,
  Select,
  Button,
  InputNumber,
  Input,
  Table,
  Modal,
  message,
  DatePicker,
  Row,
  Col,
  Descriptions,
  Tag,
  Typography,
  Divider,
} from "antd";
import { useGlobal } from "../../contexts/GlobalContext";
import request from "../../utils/request";
import dayjs from "dayjs";
import type { TableProps } from "antd";

// 添加充值码信息接口
interface RechargeCodeInfo {
  batch_no: string;
  count: number;
  created_at: string;
  expired_at: string;
  id: number;
  key: string;
  product_code: string;
  recharge_at: string;
  recharge_project: string;
  recharge_user: number;
  created_by: number;
  product_id: number;
}

interface Product {
  id: number;
  name: string;
  code: string;
  type: string;
  count: number;
}

interface RechargeRecord {
  id: number;
  batchNo: string;
  productID: number;
  amount: number;
  expiredAt: string;
  createdAt: string;
  createdBy: number;
  remark: string;
}

const RedeemCodeGenerator: React.FC = () => {
  const { API_BASE_URL, token } = useGlobal();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [records, setRecords] = useState<RechargeRecord[]>([]);
  const [generatedCodes, setGeneratedCodes] = useState<string[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [deleteForm] = Form.useForm();
  const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);
  const [deleteKeys, setDeleteKeys] = useState<string[]>([]);

  // 新增查询相关状态
  const [queryForm] = Form.useForm();
  const [queryLoading, setQueryLoading] = useState(false);
  const [codeInfo, setCodeInfo] = useState<RechargeCodeInfo | null>(null);
  const [isCodeInfoModalVisible, setIsCodeInfoModalVisible] = useState(false);

  // 获取商品列表
  const fetchProducts = useCallback(async () => {
    try {
      const response = await request.get(`${API_BASE_URL}/api/v3/recharge/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.code === 200) {
        setProducts(response.data.data.list);
      }
    } catch (error) {
      message.error("获取商品列表失败");
    }
  }, [API_BASE_URL, token]);

  // 获取充值码记录
  const fetchRechargeRecords = useCallback(async () => {
    try {
      const response = await request.get(
        `${API_BASE_URL}/api/v3/admin/recharge/record`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data.code === 200) {
        setRecords(response.data.data);
      }
    } catch (error) {
      message.error("获取充值码记录失败");
    }
  }, [API_BASE_URL, token]);

  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(generatedCodes.join("\n"));
      message.success("复制成功");
      setIsModalVisible(false);
    } catch (err) {
      message.error("复制失败");
      console.error("复制失败:", err);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchRechargeRecords();
  }, [fetchProducts, fetchRechargeRecords]);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const response = await request.post(
        `${API_BASE_URL}/api/v3/admin/recharge/add`,
        {
          amount: values.amount,
          expired_at: values.expired_at
            ? values.expired_at.format("YYYY-MM-DDTHH:mm:ssZ")
            : undefined,
          product_id: values.product_id,
          remark: values.remark,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.code === 200) {
        message.success("生成成功");
        setGeneratedCodes(response.data.data.codes);
        setIsModalVisible(true);
        fetchRechargeRecords();
      }
    } catch (error) {
      message.error("生成充值码失败");
    } finally {
      setLoading(false);
    }
  };

  // 添加一个创建者名称映射函数
  const getCreatorName = (creatorId: number) => {
    switch (creatorId) {
      case 1:
        return "Tianli";
      case 2:
        return "Heo";
      default:
        return creatorId.toString();
    }
  };

  // 查询充值码
  const handleQueryCode = async (values: { redeemCode: string }) => {
    if (!values.redeemCode.trim()) {
      message.error("请输入充值码");
      return;
    }

    setQueryLoading(true);
    try {
      const response = await request.get(
        `${API_BASE_URL}/api/v3/admin/recharge/info`,
        {
          params: { key: values.redeemCode.trim() },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.code === 200) {
        setCodeInfo(response.data.data);
        setIsCodeInfoModalVisible(true);
      } else {
        message.error(response.data.msg || "查询失败");
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        message.error("充值码不存在");
      } else {
        message.error(error.response?.data?.msg || "查询失败，请稍后重试");
      }
    } finally {
      setQueryLoading(false);
    }
  };

  // 获取充值码状态标签
  const getCodeStatusTag = (info: RechargeCodeInfo) => {
    if (info.recharge_user) {
      return <Tag color="green">已使用</Tag>;
    } else if (info.expired_at && new Date(info.expired_at) < new Date()) {
      return <Tag color="red">已过期</Tag>;
    } else {
      return <Tag color="blue">未使用</Tag>;
    }
  };

  // 修改表格列定义中的创建者ID列
  const columns: TableProps<RechargeRecord>["columns"] = [
    {
      title: "批次号",
      dataIndex: "BatchNo",
    },
    {
      title: "商品",
      dataIndex: "ProductID",
      render: (productId: number) => {
        const product = products.find((p) => p.id === productId);
        return product ? product.name : productId;
      },
    },
    {
      title: "生成数量",
      dataIndex: "Amount",
    },
    {
      title: "过期时间",
      dataIndex: "ExpiredAt",
      render: (text) => {
        if (!text || text.startsWith("0001-01-01")) {
          return "无限期";
        }
        return dayjs(text).format("YYYY-MM-DD HH:mm:ss");
      },
    },
    {
      title: "创建者",
      dataIndex: "CreatedBy",
      render: (creatorId: number) => getCreatorName(creatorId),
    },
    {
      title: "生成时间",
      dataIndex: "CreatedAt",
      render: (text) => dayjs(text).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      title: "备注",
      dataIndex: "Remark",
    },
  ];

  const handleDelete = async () => {
    try {
      const values = await deleteForm.validateFields();
      const keys = values.keys.split("\n").filter((key: string) => key.trim());

      if (keys.length === 0) {
        message.error("请输入要删除的充值码");
        return;
      }

      setDeleteKeys(keys);
      setIsDeleteConfirmVisible(true);
    } catch (error) {
      console.error("表单验证失败:", error);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await request.post(
        `${API_BASE_URL}/api/v3/admin/recharge/delete`,
        {
          keys: deleteKeys,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.code === 200) {
        message.success("充值码删除成功");
        deleteForm.resetFields();
        setIsDeleteConfirmVisible(false);
        fetchRechargeRecords();
      } else {
        message.error(response.data.msg || "删除失败");
      }
    } catch (error: any) {
      message.error(error.response?.data?.msg || "删除失败");
    }
  };

  return (
    <div
      style={{
        background: "#f0f2f5",
        minHeight: "100%",
        borderRadius: 8,
        padding: "16px",
      }}
    >
      {/* 生成充值码卡片 */}
      <Card
        title={
          <span>
            <i
              className="ri-add-circle-line"
              style={{ marginRight: "8px", color: "#1677ff" }}
            ></i>
            生成充值码
          </span>
        }
        variant="outlined"
      >
        <Form form={form} onFinish={onFinish} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="product_id"
                label="选择商品"
                rules={[{ required: true, message: "请选择商品" }]}
              >
                <Select placeholder="请选择商品">
                  {products.map((product) => (
                    <Select.Option key={product.id} value={product.id}>
                      {product.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="amount"
                label="生成数量"
                rules={[{ required: true, message: "请输入生成数量" }]}
              >
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="expired_at" label="过期时间（可选）">
                <DatePicker
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="remark"
                label="备注"
                rules={[{ required: true, message: "请输入备注信息" }]}
              >
                <Input.TextArea placeholder="请输入备注信息" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              生成充值码
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card
        title={
          <span>
            <i className="ri-search-line" style={{ marginRight: "8px", color: "#1677ff" }}></i>
            查询充值码
          </span>
        }
        style={{ marginTop: "16px" }}
        variant="outlined"
      >
        <Form form={queryForm} layout="inline" onFinish={handleQueryCode}>
          <Form.Item
            name="redeemCode"
            label="充值码"
            rules={[{ required: true, message: "请输入充值码" }]}
            style={{ width: "calc(100% - 100px)" }}
          >
            <Input placeholder="请输入要查询的充值码" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={queryLoading}>
              查询
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* 删除充值码卡片 */}
      <Card
        title={
          <span>
            <i
              className="ri-delete-bin-line"
              style={{ marginRight: "8px", color: "#1677ff" }}
            ></i>
            删除充值码
          </span>
        }
        style={{ marginTop: "16px" }}
        variant="outlined"
      >
        <Form form={deleteForm} layout="vertical">
          <Form.Item
            name="keys"
            label="充值码列表"
            rules={[{ required: true, message: "请输入要删除的充值码" }]}
          >
            <Input.TextArea
              placeholder="请输入要删除的充值码，每行一个"
              autoSize={{ minRows: 4, maxRows: 8 }}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" danger onClick={handleDelete}>
              删除充值码
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* 生成记录卡片 */}
      <Card
        title={
          <span>
            <i className="ri-history-line" style={{ marginRight: "8px" }}></i>
            生成记录
          </span>
        }
        style={{ marginTop: "16px" }}
        variant="outlined"
      >
        <Table
          columns={columns}
          dataSource={records}
          rowKey="ID"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* 生成的充值码 Modal */}
      <Modal
        title="生成的充值码"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalVisible(false)}>
            取消
          </Button>,
          <Button key="copy" type="primary" onClick={handleCopyAll}>
            复制全部
          </Button>,
        ]}
        width={600}
      >
        <div style={{ maxHeight: "400px", overflow: "auto" }}>
          {generatedCodes.map((code, index) => (
            <div key={index} style={{ marginBottom: "8px" }}>
              {code}
            </div>
          ))}
        </div>
      </Modal>

      {/* 充值码信息 Modal */}
      <Modal
        title="充值码详细信息"
        open={isCodeInfoModalVisible}
        onCancel={() => setIsCodeInfoModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsCodeInfoModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={700}
      >
        {codeInfo ? (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="充值码">
              <Typography.Text copyable>{codeInfo.key}</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              {getCodeStatusTag(codeInfo)}
            </Descriptions.Item>
            <Descriptions.Item label="批次号">
              {codeInfo.batch_no}
            </Descriptions.Item>
            <Descriptions.Item label="产品类型">
              {codeInfo.product_code === "P"
                ? "PostChat"
                : codeInfo.product_code === "S"
                ? "Summary"
                : codeInfo.product_code}
            </Descriptions.Item>
            <Descriptions.Item label="产品ID">
              {codeInfo.product_id}
            </Descriptions.Item>
            <Descriptions.Item label="充值数量">
              {codeInfo.count}
            </Descriptions.Item>
            <Descriptions.Item label="生成管理">
              {codeInfo.created_by === 1
                ? "Tianli"
                : codeInfo.created_by === 2
                ? "Heo"
                : codeInfo.created_by}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {dayjs(codeInfo.created_at).format("YYYY-MM-DD HH:mm:ss")}
            </Descriptions.Item>
            <Descriptions.Item label="过期时间">
              {codeInfo.expired_at &&
              !codeInfo.expired_at.startsWith("0001-01-01")
                ? dayjs(codeInfo.expired_at).format("YYYY-MM-DD HH:mm:ss")
                : "无限期"}
            </Descriptions.Item>

            {codeInfo.recharge_user ? (
              <>
                <Descriptions.Item>
                  <Divider orientation="left">使用信息</Divider>
                </Descriptions.Item>
                <Descriptions.Item label="使用用户">
                  {codeInfo.recharge_user}
                </Descriptions.Item>
                <Descriptions.Item label="使用时间">
                  {dayjs(codeInfo.recharge_at).format("YYYY-MM-DD HH:mm:ss")}
                </Descriptions.Item>
                <Descriptions.Item label="充值项目">
                  {codeInfo.recharge_project || "-"}
                </Descriptions.Item>
              </>
            ) : null}
          </Descriptions>
        ) : (
          <div style={{ textAlign: "center", padding: "20px" }}>加载中...</div>
        )}
      </Modal>

      <Modal
        title="确认删除"
        open={isDeleteConfirmVisible}
        onCancel={() => setIsDeleteConfirmVisible(false)}
        onOk={handleConfirmDelete}
        okText="确认删除"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        <p>确定要删除以下充值码吗？此操作不可恢复。</p>
        <div
          style={{
            maxHeight: "200px",
            overflow: "auto",
            border: "1px solid #d9d9d9",
            borderRadius: "4px",
            padding: "8px",
            marginTop: "8px",
            background: "#f5f5f5",
          }}
        >
          {deleteKeys.map((key, index) => (
            <div key={index}>{key}</div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default RedeemCodeGenerator;
